import { useLocation } from "react-router-dom";
import { RoutePaths } from "../../../../enumStrings";
import { CALLER_HANDLED_USER_ERROR_CONTEXT } from "../../../../utils/apolloCallerContext";
import { UserFacingError } from "../../../../utils/userFacingError";
import {
  CmpEventInvoiceDraftLineInput,
  CmpInvoiceKindEnum,
  CmpInvoiceStatusEnum,
  useGetCmpInvoiceDetailQuery,
} from "../../../../graphql/types";

// Monday D30 (13255070760), CMP Dashboard (15) ds/InvoicePreviewModal.jsx
// "Save Draft", D:17293-17297 and D:12891-12919: a Create Ticket / Create
// Sponsorship Invoice form saved as a Draft invoice. Edit Invoice and
// View/Approve Invoice in Manage All Invoices reopen the page filled in;
// Create updates that same invoice row.

export const EVENT_INVOICE_DRAFT_FORM_VERSION = 1;

export type EventInvoiceDraftDocumentLine = Readonly<{
  code?: string | null;
  description: string;
  note?: string | null;
  quantity: number;
  totalInCents: number;
  unitPriceInCents: number;
}>;

export const toEventInvoiceDraftLineInputs = (
  lines: readonly EventInvoiceDraftDocumentLine[],
): CmpEventInvoiceDraftLineInput[] =>
  lines.map((line) => ({
    description: line.description,
    itemCode: line.code || undefined,
    note: line.note || undefined,
    quantity: line.quantity,
    subtotalAmountInCents: Math.max(0, Math.round(line.totalInCents)),
    unitPriceInCents: Math.round(line.unitPriceInCents),
  }));

export const getEventInvoiceDraftSavedMessage = (customerName: string): string =>
  `The draft invoice for ${customerName} has been saved. You can return to it at any time to review and create the invoice.`;

export const serializeEventInvoiceDraftForm = (form: object): string =>
  JSON.stringify({ ...form, version: EVENT_INVOICE_DRAFT_FORM_VERSION });

export const parseEventInvoiceDraftForm = <Form extends object>(
  value: string | null | undefined,
): Form | null => {
  try {
    const parsed = JSON.parse(value || "");
    return parsed && typeof parsed === "object" &&
      parsed.version === EVENT_INVOICE_DRAFT_FORM_VERSION
      ? (parsed as Form)
      : null;
  } catch {
    return null;
  }
};

/** Manage All Invoices Edit / View/Approve for an event invoice draft. */
export const getEventInvoiceDraftPath = ({
  invoiceId,
  invoiceKind,
  preview = false,
}: {
  invoiceId: string;
  invoiceKind: CmpInvoiceKindEnum | null | undefined;
  preview?: boolean;
}): string | null => {
  const basePath =
    invoiceKind === CmpInvoiceKindEnum.EventRegistration
      ? RoutePaths.chamberAdmin_events_ticketSalesCreate
      : invoiceKind === CmpInvoiceKindEnum.EventSponsorship
        ? RoutePaths.chamberAdmin_events_sponsorshipSalesCreate
        : null;
  if (!basePath) return null;
  return `${basePath}?draftInvoiceId=${encodeURIComponent(invoiceId)}${preview ? "&preview=1" : ""}`;
};

export const isEventInvoiceDraftRow = (row: {
  invoiceKind?: CmpInvoiceKindEnum | null;
}): boolean =>
  row.invoiceKind === CmpInvoiceKindEnum.EventRegistration ||
  row.invoiceKind === CmpInvoiceKindEnum.EventSponsorship;

/** The draft named in ?draftInvoiceId= on a create page (strong read). */
export const useEventInvoiceDraftFromRoute = ({
  chamberId,
  enabled,
}: {
  chamberId: string;
  enabled: boolean;
}) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const draftInvoiceId = enabled ? params.get("draftInvoiceId") : null;
  const { data, error, loading } = useGetCmpInvoiceDetailQuery({
    variables: { input: { chamberId, invoiceId: draftInvoiceId || "" } },
    skip: !chamberId || !draftInvoiceId,
    fetchPolicy: "network-only",
    // The create page shows this error in its pop-up instead of /500.
    context: CALLER_HANDLED_USER_ERROR_CONTEXT,
  });
  const invoice = data?.getCmpInvoiceDetail.invoice;
  const draft =
    invoice && invoice.invoiceStatus === CmpInvoiceStatusEnum.Draft &&
    invoice.eventInvoiceDraftForm
      ? invoice
      : null;

  return {
    draft,
    draftInvoiceId,
    error:
      error ||
      (draftInvoiceId && !loading && data && !draft
        ? new UserFacingError("This draft is no longer available. It may have been made into an invoice or deleted. Go to Invoices & Payments > Manage all Invoices & Payments to find it.")
        : null),
    loading,
    openPreview: params.get("preview") === "1",
  };
};
